import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  authHeaders,
  getAuthToken,
  getCurrentUser,
  handleUnauthorized,
} from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTREACH_STAGE_LABELS = {
  not_contacted: "Not Contacted",
  initial_contact: "Initial Contact",
  followup_1: "Follow-up 1",
  followup_2: "Follow-up 2",
  followup_3: "Follow-up 3",
  followup_4: "Follow-up 4",
  followup_5: "Follow-up 5",
  followup_6: "Follow-up 6",
  final_followup: "Final Follow-up",
  final_personal_attempt: "Final Personal Attempt",
  closed_no_response: "Closed / No Response",
  replied: "Replied",
  activated: "Activated",
};

function outreachStageLabel(value) {
  return OUTREACH_STAGE_LABELS[value] || "Not Contacted";
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function formatDateOnly(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
}

function outreachAttempts(lead) {
  return Array.isArray(lead.outreach_history)
    ? lead.outreach_history.length
    : Number(lead.outreach_attempt_count || 0);
}

function cleanInstagramHandle(value) {
  const raw = String(value || "").trim();

  if (!raw) return "";

  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      const host = url.hostname.replace(/^www\./i, "").toLowerCase();

      if (host.includes("instagram.com")) {
        return url.pathname.split("/").filter(Boolean)[0] || raw;
      }
    } catch {
      return raw;
    }
  }

  return raw.replace(/^@/, "");
}

function sourceUrlLabel(value) {
  const raw = String(value || "").trim();

  if (!raw) return "Website";

  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();

    if (host.includes("facebook.com")) return "Facebook";
    if (host.includes("instagram.com")) return "Instagram";
  } catch {
    // Keep the safe generic label below.
  }

  return "Website";
}

function sourceUrlHref(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (/^https?:\/\//i.test(raw)) return raw;

  return `https://${raw}`;
}

const CRM_STATUS_LABELS = {
  new: "New",
  working: "Working",
  replied: "Replied",
  won: "Won",
  lost: "Lost",
  closed_no_response: "Closed / No Response",
};

function normalizeCrmStatus(value) {
  const status = (value || "new").toLowerCase();

  if (
    status === "working" ||
    status === "contacted" ||
    status === "follow_up_1" ||
    status === "follow_up_2"
  ) {
    return "working";
  }

  if (status === "replied") return "replied";
  if (status === "won") return "won";
  if (status === "lost") return "lost";
  if (
    status === "closed_no_response" ||
    status === "closed-no-response"
  ) {
    return "closed_no_response";
  }

  return "new";
}

function crmStatusLabel(value) {
  return CRM_STATUS_LABELS[normalizeCrmStatus(value)] || "New";
}

export default function AdminLeads({ staffMode = false }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const currentUser = getCurrentUser();
  useEffect(() => {
  const authToken = getAuthToken();

  if (!authToken || !currentUser) {
  handleUnauthorized();
  return;
}

  if (staffMode && currentUser.role === "owner") {
    window.location.replace("/admin/leads");
    return;
  }

  if (!staffMode && currentUser.role !== "owner") {
    window.location.replace("/staff/leads");
  }
}, [staffMode, currentUser]);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [openNotesLeadId, setOpenNotesLeadId] = useState("");
  const topScrollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const syncingScrollRef = useRef(false);

  useEffect(() => {
  loadLeads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [staffMode]);

 async function loadLeads() {
  try {
    setLoading(true);

    const authToken = getAuthToken();

const leadUrl = staffMode
  ? `${API}/staff/leads`
  : `${API}/leads`;

const leadHeaders = staffMode
  ? {
      Authorization: `Bearer ${authToken}`,
    }
  : authHeaders();

const leadRes = await fetch(leadUrl, {
  headers: leadHeaders,
});

let userRes = null;

if (!staffMode) {
  userRes = await fetch(`${API}/users`, {
    headers: authHeaders(),
  });
}

    if (
  leadRes.status === 401 ||
  leadRes.status === 403 ||
  userRes?.status === 401 ||
  userRes?.status === 403
) {
  handleUnauthorized();
  return;
}

if (!leadRes.ok) {
  throw new Error(`HTTP ${leadRes.status}`);
}

    const data = await leadRes.json();
    const normalizedLeads = Array.isArray(data)
      ? data.map((lead) => ({
          ...lead,
          followup_status: normalizeCrmStatus(
            lead.followup_status
          ),
        }))
      : [];

    setLeads(normalizedLeads);

    if (userRes?.ok) {
      const userData = await userRes.json();
      setUsers(userData);
}
  } catch (err) {
    console.error(err);
    setError("Failed to load leads");
  } finally {
    setLoading(false);
  }
}
  
async function saveFollowup(lead) {
  try {
const authToken = getAuthToken();

const followupUrl = staffMode
  ? `${API}/staff/leads/${lead.id}/followup`
  : `${API}/admin/leads/${lead.id}/followup`;

const followupHeaders = staffMode
  ? {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    }
  : {
      "Content-Type": "application/json",
      ...authHeaders(),
    };

const res = await fetch(
  followupUrl,
  {
    method: "PUT",
    headers: followupHeaders,
        body: JSON.stringify({
          followup_status: lead.followup_status,
          followup_notes: lead.followup_notes,
          next_followup_at: lead.next_followup_at,
          last_contacted_at: new Date().toISOString(),
        }),
      }
    );

    if (res.status === 401 || res.status === 403) {
  handleUnauthorized();
  return;
}

if (!res.ok) {
  throw new Error("Save failed");
}

    alert("Saved");
  } catch (err) {
    console.error(err);
    alert("Could not save");
  }
}
  async function deleteLead(lead) {
  if (!window.confirm(`Delete "${lead.restaurant_name || "this lead"}"? This cannot be undone.`)) {
    return;
  }

  try {

    const res = await fetch(`${API}/admin/leads/${lead.id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });

    if (res.status === 401 || res.status === 403) {
  handleUnauthorized();
  return;
}

if (!res.ok) {
  throw new Error("Delete failed");
}

    setLeads((prev) => prev.filter((l) => l.id !== lead.id));
  } catch (err) {
    console.error(err);
    alert("Could not delete lead");
  }
}
  function toggleLeadSelection(leadId) {
    setSelectedLeadIds((current) =>
      current.includes(leadId)
        ? current.filter((id) => id !== leadId)
        : [...current, leadId]
    );
  }

  function clearLeadSelection() {
    setSelectedLeadIds([]);
  }

  async function deleteSelectedLeads() {
    if (staffMode || selectedLeadIds.length === 0) return;

    const count = selectedLeadIds.length;

    if (
      !window.confirm(
        `Delete ${count} selected lead${count === 1 ? "" : "s"}? This cannot be undone.`
      )
    ) {
      return;
    }

    const deletedIds = [];
    const failedIds = [];

    for (const leadId of selectedLeadIds) {
      try {
        const res = await fetch(`${API}/admin/leads/${leadId}`, {
          method: "DELETE",
          headers: authHeaders(),
        });

        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }

        if (!res.ok) {
          failedIds.push(leadId);
          continue;
        }

        deletedIds.push(leadId);
      } catch (err) {
        console.error(err);
        failedIds.push(leadId);
      }
    }

    if (deletedIds.length > 0) {
      const deletedSet = new Set(deletedIds);

      setLeads((current) =>
        current.filter((lead) => !deletedSet.has(lead.id))
      );

      setSelectedLeadIds((current) =>
        current.filter((id) => !deletedSet.has(id))
      );
    }

    if (failedIds.length > 0) {
      alert(
        `${failedIds.length} selected lead${
          failedIds.length === 1 ? "" : "s"
        } could not be deleted.`
      );
    }
  }

  function syncHorizontalScroll(source, target) {
    if (!source || !target || syncingScrollRef.current) return;

    const sourceMax = Math.max(
      0,
      source.scrollWidth - source.clientWidth
    );
    const targetMax = Math.max(
      0,
      target.scrollWidth - target.clientWidth
    );

    syncingScrollRef.current = true;

    const ratio =
      sourceMax > 0 ? source.scrollLeft / sourceMax : 0;

    target.scrollLeft = ratio * targetMax;

    window.requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  }

  const today = new Date().toISOString().slice(0, 10);

  function handleSort(field) {
  if (sortField === field) {
    setSortDirection((current) =>
      current === "asc" ? "desc" : "asc"
    );
  } else {
    setSortField(field);
    setSortDirection("asc");
  }
}

const stats = {
  dueToday: leads.filter(
    (lead) => lead.next_followup_at === today
  ).length,
  overdue: leads.filter(
    (lead) =>
      Boolean(lead.next_followup_at) &&
      lead.next_followup_at < today
  ).length,
  new: leads.filter(
    (lead) =>
      normalizeCrmStatus(lead.followup_status) === "new"
  ).length,
  working: leads.filter(
    (lead) =>
      normalizeCrmStatus(lead.followup_status) === "working"
  ).length,
  replied: leads.filter(
    (lead) =>
      normalizeCrmStatus(lead.followup_status) === "replied"
  ).length,
  won: leads.filter(
    (lead) =>
      normalizeCrmStatus(lead.followup_status) === "won"
  ).length,
  lost: leads.filter(
    (lead) =>
      normalizeCrmStatus(lead.followup_status) === "lost"
  ).length,
  closedNoResponse: leads.filter(
    (lead) =>
      normalizeCrmStatus(lead.followup_status) ===
      "closed_no_response"
  ).length,
};

const visibleLeads = leads.filter((lead) => {
  const status = normalizeCrmStatus(lead.followup_status);

  let matchesFilter = true;

  if (filter === "dueToday") {
    matchesFilter = lead.next_followup_at === today;
  } else if (filter === "overdue") {
    matchesFilter =
      Boolean(lead.next_followup_at) && lead.next_followup_at < today;
  } else if (filter === "new") {
    matchesFilter = status === "new";
  } else if (filter === "working") {
    matchesFilter = status === "working";
  } else if (filter === "replied") {
    matchesFilter = status === "replied";
  } else if (filter === "won") {
    matchesFilter = status === "won";
  } else if (filter === "lost") {
    matchesFilter = status === "lost";
  } else if (filter === "closed_no_response") {
    matchesFilter = status === "closed_no_response";
  }

  if (!matchesFilter) return false;
  if (assignedFilter !== "all") {
  const leadAssignedTo = lead.assigned_to || "owner";

  if (leadAssignedTo !== assignedFilter) {
    return false;
  }
}

  if (dateFilter !== "all") {
  const createdDate = lead.created_at ? new Date(lead.created_at) : null;

  if (!createdDate || Number.isNaN(createdDate.getTime())) {
    return false;
  }

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );

  if (dateFilter === "today") {
    if (createdDate < startOfToday) return false;
  }

  if (dateFilter === "this_week") {
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());

    if (createdDate < startOfWeek) return false;
  }

  if (dateFilter === "this_month") {
    if (
      createdDate.getMonth() !== now.getMonth() ||
      createdDate.getFullYear() !== now.getFullYear()
    ) {
      return false;
    }
  }

  if (dateFilter === "last_month") {
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

    if (
      createdDate.getMonth() !== lastMonth.getMonth() ||
      createdDate.getFullYear() !== lastMonth.getFullYear()
    ) {
      return false;
    }
  }

  if (
    dateFilter === "this_year" &&
    createdDate.getFullYear() !== now.getFullYear()
  ) {
    return false;
  }
}

  const query = searchTerm.trim().toLowerCase();

  if (!query) return true;

  const assignedUser = users.find((user) => user.id === lead.assigned_to);
  const assignedName =
    lead.assigned_to === "owner" || !lead.assigned_to
      ? "Owner"
      : assignedUser?.name ||
        lead.owner_email ||
        lead.assigned_to ||
        "";

  const createdDate = lead.created_at
    ? new Date(lead.created_at)
    : null;

  const createdSearchText =
    createdDate && !Number.isNaN(createdDate.getTime())
      ? [
          createdDate.toLocaleDateString("en-US"),
          createdDate.toLocaleString("en-US", { month: "long" }),
          createdDate.toLocaleString("en-US", { month: "short" }),
          createdDate.toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          }),
          createdDate.getFullYear().toString(),
          `${String(createdDate.getMonth() + 1).padStart(
            2,
            "0"
          )}/${createdDate.getFullYear()}`,
        ].join(" ")
      : "";

  const searchableText = [
    lead.restaurant_name,
    lead.contact_name,
    lead.email,
    lead.phone,
    lead.city,
    lead.state,
    lead.instagram_handle,
    lead.website_url,
    lead.source_reference,
    lead.cta_link,
    lead.offer,
    status,
    crmStatusLabel(status),
    lead.followup_status,
    String(lead.followup_status || "").replaceAll("_", " "),
    lead.outreach_stage,
    outreachStageLabel(lead.outreach_stage),
    lead.last_contacted_at,
    assignedName,
    createdSearchText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

    return searchableText.includes(query);
}).sort((a, b) => {
  let aValue = a[sortField] ?? "";
  let bValue = b[sortField] ?? "";

if (sortField === "assigned_to") {
  const aUser = users.find((user) => user.id === a.assigned_to);
  const bUser = users.find((user) => user.id === b.assigned_to);

  aValue =
    a.assigned_to === "owner" || !a.assigned_to
      ? "Owner"
      : aUser?.name || a.owner_email || a.assigned_to || "";

  bValue =
    b.assigned_to === "owner" || !b.assigned_to
      ? "Owner"
      : bUser?.name || b.owner_email || b.assigned_to || "";
}

  if (sortField === "created_at" || sortField === "next_followup_at") {
    aValue = aValue ? new Date(aValue).getTime() : 0;
    bValue = bValue ? new Date(bValue).getTime() : 0;
  } else {
    aValue = String(aValue).toLowerCase();
    bValue = String(bValue).toLowerCase();
  }

  if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
  if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
  return 0;
});
  
  const visibleLeadIds = visibleLeads
    .map((lead) => lead.id)
    .filter(Boolean);

  const allVisibleSelected =
    visibleLeadIds.length > 0 &&
    visibleLeadIds.every((id) => selectedLeadIds.includes(id));

  function toggleSelectAllVisible() {
    if (allVisibleSelected) {
      setSelectedLeadIds((current) =>
        current.filter((id) => !visibleLeadIds.includes(id))
      );
      return;
    }

    setSelectedLeadIds((current) =>
      Array.from(new Set([...current, ...visibleLeadIds]))
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-white p-6">
      <div className="w-full max-w-[1800px] mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
         <div>
            <h1 className="text-3xl font-bold">
  {staffMode ? "My Leads" : "Lead Submissions"}
</h1>

<p className="mt-1 text-zinc-400">
  {staffMode
    ? "Leads assigned to your account."
    : "Restaurant signup submissions from the public form."}
</p>

<p className="mt-2 max-w-3xl text-sm text-zinc-500">
  Outreach status is shown here for reference. Use Restaurant Pages to run the daily follow-up workflow and send messages.
</p>

{staffMode && currentUser && (
  <div className="mt-2 text-sm text-zinc-400">
    Signed in as{" "}
    <span className="font-semibold text-white">
      {currentUser.name}
    </span>
  </div>
)}

<div className="mt-4 mb-6 flex gap-3">
  <a
    href={staffMode ? "/staff/mockups" : "/admin/mockups"}
    className="rounded-xl bg-cyan-600 px-4 py-2 text-sm font-semibold hover:bg-cyan-500"
  >
    ← Back to Restaurant Pages
  </a>
</div>

          </div>

          <button
            type="button"
            onClick={handleUnauthorized}
            className="shrink-0 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-300 hover:bg-red-500/20"
          >
            Logout
          </button>
        </div>
<div className="grid grid-cols-2 gap-3 mb-6 md:grid-cols-4 xl:grid-cols-8">
  <button
    onClick={() => setFilter("dueToday")}
    className="rounded-xl bg-cyan-600 p-4 text-left"
  >
    <div className="text-2xl font-bold">{stats.dueToday}</div>
    <div className="text-sm text-cyan-100">Due Today</div>
  </button>

  <button
    onClick={() => setFilter("overdue")}
    className="rounded-xl bg-red-600 p-4 text-left"
  >
    <div className="text-2xl font-bold">{stats.overdue}</div>
    <div className="text-sm text-red-100">Overdue</div>
  </button>

  <button
    onClick={() => setFilter("new")}
    className="rounded-xl bg-zinc-800 p-4 text-left"
  >
    <div className="text-2xl font-bold">{stats.new}</div>
    <div className="text-sm text-zinc-300">New</div>
  </button>

  <button
    onClick={() => setFilter("working")}
    className="rounded-xl bg-blue-700 p-4 text-left"
  >
    <div className="text-2xl font-bold">{stats.working}</div>
    <div className="text-sm text-blue-100">Working</div>
  </button>

  <button
    onClick={() => setFilter("replied")}
    className="rounded-xl bg-violet-700 p-4 text-left"
  >
    <div className="text-2xl font-bold">{stats.replied}</div>
    <div className="text-sm text-violet-100">Replied</div>
  </button>

  <button
    onClick={() => setFilter("won")}
    className="rounded-xl bg-green-700 p-4 text-left"
  >
    <div className="text-2xl font-bold">{stats.won}</div>
    <div className="text-sm text-green-100">Won</div>
  </button>

  <button
    onClick={() => setFilter("lost")}
    className="rounded-xl bg-zinc-700 p-4 text-left"
  >
    <div className="text-2xl font-bold">{stats.lost}</div>
    <div className="text-sm text-zinc-300">Lost</div>
  </button>

  <button
    onClick={() => setFilter("closed_no_response")}
    className="rounded-xl bg-stone-800 p-4 text-left"
  >
    <div className="text-2xl font-bold">{stats.closedNoResponse}</div>
    <div className="text-sm text-stone-300">Closed / No Response</div>
  </button>
</div>

<div className="flex flex-wrap gap-2 mb-6">
  {[
    ["all", "All"],
    ["new", "New"],
    ["working", "Working"],
    ["replied", "Replied"],
    ["won", "Won"],
    ["lost", "Lost"],
    ["closed_no_response", "Closed / No Response"],
  ].map(([value, label]) => (
    <button
      key={value}
      type="button"
      onClick={() => setFilter(value)}
      className={`rounded px-3 py-2 text-sm transition-colors ${
        filter === value
          ? "bg-cyan-500/20 text-cyan-200 ring-1 ring-cyan-500/40"
          : "bg-white/10 hover:bg-white/20"
      }`}
    >
      {label}
    </button>
  ))}
</div>
        <div
  className={`mb-6 grid gap-3 ${
    staffMode
      ? "md:grid-cols-[1fr_220px_auto]"
      : "md:grid-cols-[1fr_220px_220px_auto]"
  }`}
>
  <input
    type="text"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    placeholder="Search leads by restaurant, contact, email, phone, city, status, staff, month, or year..."
    className="w-full rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-sm text-white placeholder-zinc-500 focus:border-cyan-400 focus:outline-none"
  />

  {!staffMode && (
  <select
    value={assignedFilter}
    onChange={(e) => setAssignedFilter(e.target.value)}
    className="rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
  >
    <option value="all">Everyone</option>
    <option value="owner">Owner</option>

    {users.map((user) => (
      <option key={user.id} value={user.id}>
        {user.name}
      </option>
    ))}
  </select>
)}
  <select
    value={dateFilter}
    onChange={(e) => setDateFilter(e.target.value)}
    className="rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
  >
    <option value="all">All Time</option>
    <option value="today">Today</option>
    <option value="this_week">This Week</option>
    <option value="this_month">This Month</option>
    <option value="last_month">Last Month</option>
    <option value="this_year">This Year</option>
  </select>

  {(searchTerm || assignedFilter !== "all" || dateFilter !== "all") && (
    <button
      type="button"
      onClick={() => {
        setSearchTerm("");
        setAssignedFilter("all");
        setDateFilter("all");
      }}
      className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-300 hover:bg-white/10"
    >
      Clear
    </button>
  )}
</div>

        {!loading && !error && (
          <div className="max-w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0D0D0F]">
            {!staffMode && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
                <div className="text-sm text-zinc-400">
                  {selectedLeadIds.length > 0
                    ? `${selectedLeadIds.length} lead${
                        selectedLeadIds.length === 1 ? "" : "s"
                      } selected`
                    : "Select leads below to manage them in bulk."}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={toggleSelectAllVisible}
                    disabled={visibleLeads.length === 0}
                    className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-zinc-200 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {allVisibleSelected
                      ? "Clear Visible Selection"
                      : "Select All Visible"}
                  </button>

                  {selectedLeadIds.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={clearLeadSelection}
                        className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-zinc-300 hover:bg-white/10"
                      >
                        Clear Selection
                      </button>

                      <button
                        type="button"
                        onClick={deleteSelectedLeads}
                        className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300 hover:bg-red-500/20"
                      >
                        Delete Selected
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="border-b border-white/10 bg-[#151517] px-2 py-2">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">
                Scroll Table Left / Right
              </div>

              <div
                ref={topScrollRef}
                className="h-6 w-full overflow-x-scroll overflow-y-hidden"
                onScroll={(event) =>
                  syncHorizontalScroll(
                    event.currentTarget,
                    tableScrollRef.current
                  )
                }
                aria-label="Horizontal table scroll"
              >
                <div className="h-px w-[2600px]" />
              </div>
            </div>

            <div
              ref={tableScrollRef}
              className="max-h-[68vh] w-full overflow-auto"
              style={{ scrollbarGutter: "stable" }}
              onScroll={(event) =>
                syncHorizontalScroll(
                  event.currentTarget,
                  topScrollRef.current
                )
              }
            >
              <table className="min-w-[1720px] w-full text-sm">
              <thead className="sticky top-0 z-10 bg-[#171719]">
                <tr>
                  {!staffMode && (
                    <th className="sticky left-0 z-20 w-12 bg-[#171719] p-4 text-center">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                        aria-label="Select all visible leads"
                        className="h-4 w-4 cursor-pointer accent-cyan-400"
                      />
                    </th>
                  )}

                  <th className="text-left p-4">
  <button
    type="button"
    onClick={() => handleSort("restaurant_name")}
    className="inline-flex items-center gap-1 hover:text-cyan-300"
  >
    Restaurant
    {sortField === "restaurant_name" && (
      <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
    )}
  </button>
</th>
                  <th className="text-left p-4">Contact / Source</th>
                  <th className="w-[170px] text-left p-4">Email</th>
                  <th className="text-left p-4">Phone</th>
                  <th className="text-left p-4">City</th>
                  <th className="text-left p-4">Promotion / Event</th>
                  <th className="text-left p-4">
  <button
    type="button"
    onClick={() => handleSort("assigned_to")}
    className="inline-flex items-center gap-1 hover:text-cyan-300"
  >
    Assigned To
    {sortField === "assigned_to" && (
      <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
    )}
  </button>
</th>
                  <th className="text-left p-4">
  <button
    type="button"
    onClick={() => handleSort("followup_status")}
    className="inline-flex items-center gap-1 hover:text-cyan-300"
  >
    CRM Status
    {sortField === "followup_status" && (
      <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
    )}
  </button>
</th>
                  <th className="text-left p-4">Outreach Stage</th>
                  <th className="w-[110px] text-left p-4">Last Contacted</th>
                  <th className="text-left p-4">Attempts</th>
                  <th className="text-left p-4">
  <button
    type="button"
    onClick={() => handleSort("next_followup_at")}
    className="inline-flex items-center gap-1 hover:text-cyan-300"
  >
    Next Follow-Up
    {sortField === "next_followup_at" && (
      <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
    )}
  </button>
</th>
                  <th className="w-[110px] text-left p-4">Notes</th>
                  <th className="text-left p-4">Outreach</th>
                  <th className="text-left p-4">
  <button
    type="button"
    onClick={() => handleSort("created_at")}
    className="inline-flex items-center gap-1 hover:text-cyan-300"
  >
    Created
    {sortField === "created_at" && (
      <span>{sortDirection === "asc" ? "▲" : "▼"}</span>
    )}
  </button>
</th>
                </tr>
              </thead>

              <tbody>
                {visibleLeads.map((lead, idx) => (
                  <tr
                    key={lead.id || idx}
                    className={`border-t hover:bg-white/5 ${
                      lead.restaurant_page_deleted
                        ? "border-red-500/30 bg-red-500/[0.045]"
                        : "border-white/5"
                    }`}
                  >
                    {!staffMode && (
                      <td className="sticky left-0 z-[5] bg-[#0D0D0F] p-4 text-center">
                        <input
                          type="checkbox"
                          checked={selectedLeadIds.includes(lead.id)}
                          onChange={() => toggleLeadSelection(lead.id)}
                          aria-label={`Select ${
                            lead.restaurant_name || "lead"
                          }`}
                          className="h-4 w-4 cursor-pointer accent-cyan-400"
                        />
                      </td>
                    )}

                    <td className="p-4 font-medium">
                      <div>{lead.restaurant_name || "-"}</div>

                      {lead.restaurant_page_deleted ? (
                        <div className="mt-1 inline-flex rounded-full border border-red-500/40 bg-red-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-red-300">
                          Restaurant Page Deleted
                        </div>
                      ) : null}
                    </td>

                    <td className="p-4">
                      <div className="min-w-[190px] space-y-1">
                        {lead.contact_name ? (
                          <div className="font-medium text-zinc-100">
                            {lead.contact_name}
                          </div>
                        ) : null}

                        {cleanInstagramHandle(lead.instagram_handle) ? (
                          <a
                            href={`https://www.instagram.com/${cleanInstagramHandle(
                              lead.instagram_handle
                            )}/`}
                            target="_blank"
                            rel="noreferrer"
                            className="block text-xs font-semibold text-pink-300 hover:text-pink-200 hover:underline"
                          >
                            @{cleanInstagramHandle(lead.instagram_handle)}
                          </a>
                        ) : null}

                        {lead.website_url ? (
                          <a
                            href={sourceUrlHref(lead.website_url)}
                            target="_blank"
                            rel="noreferrer"
                            className="block max-w-[220px] truncate text-xs text-cyan-300 hover:text-cyan-200 hover:underline"
                            title={lead.website_url}
                          >
                            {sourceUrlLabel(lead.website_url)}:{" "}
                            {lead.website_url}
                          </a>
                        ) : null}

                        {!lead.contact_name &&
                        !cleanInstagramHandle(lead.instagram_handle) &&
                        !lead.website_url &&
                        lead.source_reference ? (
                          <div
                            className="max-w-[220px] truncate text-xs text-zinc-300"
                            title={lead.source_reference}
                          >
                            Source: {lead.source_reference}
                          </div>
                        ) : null}

                        {!lead.contact_name &&
                        !cleanInstagramHandle(lead.instagram_handle) &&
                        !lead.website_url &&
                        !lead.source_reference ? (
                          <span className="text-xs text-zinc-500">
                            No contact/source saved
                          </span>
                        ) : null}
                      </div>
                    </td>


                    <td className="w-[170px] p-4">
                      <div
                        className="max-w-[145px] truncate text-zinc-200"
                        title={lead.email || ""}
                      >
                        {lead.email || "-"}
                      </div>
                    </td>

                    <td className="p-4">
                      {lead.phone || "-"}
                    </td>

                    <td className="p-4">
                      {lead.city || "-"}
                    </td>

                    <td className="p-4">
                      {lead.offer || "-"}
                    </td>

                    <td className="p-4">
  <span className="inline-flex rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-xs text-cyan-300">
    {staffMode
  ? currentUser?.name || "Staff"
  : lead.assigned_to === "owner" || !lead.assigned_to
  ? "Owner"
  : users.find((u) => u.id === lead.assigned_to)?.name ||
    lead.owner_email ||
    lead.assigned_to}
  </span>
</td>
                    
<td className="p-4">
  <select
    className={`rounded border px-2 py-1 ${
      normalizeCrmStatus(lead.followup_status) === "won"
        ? "border-green-500 bg-green-700"
        : normalizeCrmStatus(lead.followup_status) === "closed_no_response"
        ? "border-stone-500 bg-stone-800"
        : normalizeCrmStatus(lead.followup_status) === "lost"
        ? "border-red-500 bg-red-700"
        : normalizeCrmStatus(lead.followup_status) === "replied"
        ? "border-violet-500 bg-violet-700"
        : normalizeCrmStatus(lead.followup_status) === "working"
        ? "border-blue-500 bg-blue-700"
        : "border-zinc-700 bg-zinc-800"
    }`}
    value={normalizeCrmStatus(lead.followup_status)}
    onChange={(event) => {
      setLeads((previous) =>
        previous.map((item) =>
          item.id === lead.id
            ? {
                ...item,
                followup_status: event.target.value,
              }
            : item
        )
      );
    }}
  >
    <option value="new">New</option>
    <option value="working">Working</option>
    <option value="replied">Replied</option>
    <option value="won">Won</option>
    <option value="lost">Lost</option>
    <option value="closed_no_response">Closed / No Response</option>
  </select>
</td>

<td className="p-4">
  <span className="inline-flex whitespace-nowrap rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-1 text-xs font-semibold text-violet-300">
    {outreachStageLabel(lead.outreach_stage)}
  </span>
</td>

<td className="w-[110px] p-4 text-zinc-300">
  <span
    className="whitespace-nowrap"
    title={formatDateTime(lead.last_contacted_at)}
  >
    {formatDateOnly(lead.last_contacted_at)}
  </span>
</td>

<td className="p-4 text-center font-semibold text-zinc-200">
  {outreachAttempts(lead)}
</td>

<td className="p-4">
  <div className="flex flex-col gap-2">
    <input
      type="date"
      className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1"
      value={lead.next_followup_at || ""}
      onChange={(e) => {
        setLeads((prev) =>
          prev.map((l) =>
            l.id === lead.id
              ? { ...l, next_followup_at: e.target.value }
              : l
          )
        );
      }}
    />

    <button
      onClick={() => saveFollowup(lead)}
      className="bg-cyan-600 hover:bg-cyan-500 px-2 py-1 rounded text-xs"
    >
      Save
    </button>
    
    {!staffMode && (
  <button
    onClick={() => deleteLead(lead)}
    className="bg-red-700 hover:bg-red-600 px-2 py-1 rounded text-xs"
  >
    Delete
  </button>
)}
    
  </div>
</td>
                    <td className="p-4">
  <div className="relative w-[90px]">
    <button
      type="button"
      onClick={() =>
        setOpenNotesLeadId((current) =>
          current === lead.id ? "" : lead.id
        )
      }
      className={`w-full rounded-lg border px-3 py-2 text-xs font-semibold ${
        lead.followup_notes
          ? "border-cyan-500/30 bg-cyan-500/10 text-cyan-300"
          : "border-white/10 bg-white/5 text-zinc-300"
      } hover:bg-white/10`}
    >
      {lead.followup_notes ? "Notes ✓" : "Notes"}
    </button>

    {openNotesLeadId === lead.id ? (
      <div className="absolute left-0 top-full z-40 mt-2 w-[320px] rounded-xl border border-white/10 bg-[#18181C] p-3 shadow-2xl">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-xs font-semibold text-white">
            Follow-Up Notes
          </div>
          <button
            type="button"
            onClick={() => setOpenNotesLeadId("")}
            className="text-xs text-zinc-400 hover:text-white"
          >
            Close
          </button>
        </div>

        <textarea
          className="min-h-[130px] w-full resize-y rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white focus:border-cyan-400 focus:outline-none"
          placeholder="Notes..."
          value={lead.followup_notes || ""}
          onChange={(e) => {
            setLeads((prev) =>
              prev.map((l) =>
                l.id === lead.id
                  ? {
                      ...l,
                      followup_notes: e.target.value,
                    }
                  : l
              )
            );
          }}
        />

        <button
          type="button"
          onClick={async () => {
            await saveFollowup(lead);
            setOpenNotesLeadId("");
          }}
          className="mt-2 w-full rounded-lg bg-cyan-600 px-3 py-2 text-xs font-semibold text-white hover:bg-cyan-500"
        >
          Save Notes
        </button>
      </div>
    ) : null}
  </div>
</td>
<td className="p-4">
  {lead.restaurant_page_deleted ? (
    <div className="min-w-[155px]">
      <div className="inline-flex whitespace-nowrap rounded-full border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-300">
        Restaurant Page Deleted
      </div>
      <div className="mt-1 max-w-[180px] text-[10px] leading-relaxed text-zinc-500">
        The lead is preserved. Delete it manually if no longer needed, or rebuild a new Restaurant Page.
      </div>
    </div>
  ) : lead.source_mockup_id && lead.restaurant_page_exists !== false ? (
    <Link
      to={
        staffMode
          ? `/staff/mockups/${lead.source_mockup_id}/edit`
          : `/admin/mockups/${lead.source_mockup_id}/edit`
      }
      className="inline-flex whitespace-nowrap rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-500/20"
    >
      {normalizeCrmStatus(lead.followup_status) === "closed_no_response"
        ? "Reopen Outreach"
        : "Continue Outreach"}
    </Link>
  ) : (
    <span className="text-xs text-zinc-500">No Restaurant Page</span>
  )}
</td>
                    <td className="p-4 text-zinc-400">
                      {lead.created_at
                        ? new Date(lead.created_at).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                ))}

                {visibleLeads.length === 0 && (
                  <tr>
                    <td
                      colSpan={staffMode ? 15 : 16}
                      className="p-10 text-center text-zinc-500"
                    >
                      No leads found.
                    </td>
                  </tr>
                )}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
